import { EDITOR_VIEW_NAME } from "../../core/constants";
import { getViewData, setViewData } from "../../core/views";
import { highlightLine, removeAllHighlights } from '../../components/MonacoEditorHelper';
import { getDomainCfg } from '../../core/state';

function findAnchor(oldCode: string, newCode: string): boolean {
  if (!oldCode.trim()) {
    return false; // Bestandscode ist leer
  }

  const oldLines = oldCode.split(/\r?\n/);
  const newLines = newCode.split(/\r?\n/);

  // Suche nach einem gemeinsamen Anker (eine identische, nicht-leere Zeile)
  const hasAnchor = oldLines.some(oldLine =>
    oldLine.trim() && newLines.includes(oldLine)
  );

  return hasAnchor; // Ersetze den gesamten Code, wenn kein Anker gefunden wurde
}

function replaceEntireCode(newCode: string): string {
  console.log("Ersetze den gesamten Code");
  const lines = newCode.split(/\r?\n/);
  lines.forEach((line, index) => {
    highlightLine(index, "highlight-new", 3000);
  });
  return newCode;
}

function appendCode(oldCode: string, newCode: string): string {
  const oldLineCount = oldCode.split(/\r?\n/).length;
  const lines = newCode.split(/\r?\n/);
  lines.forEach((line, index) => {
    highlightLine(oldLineCount + index, "highlight-new", 3000);
  });
  return oldCode + "\n" + newCode;
}

function compareAndMerge(oldCode, newCode) {
  const oldLines = oldCode.split(/\r?\n/);
  const newLines = newCode.split(/\r?\n/);
  let mergedCode = [];
  let i = 0, j = 0;
  let anchorFound = false;

  // Hilfsfunktion, um relevante Zeilen zu identifizieren (keine leeren Zeilen oder trivialen Kommentare)
  function isRelevantLine(line) {
    return line && !line.startsWith('//') && line.trim().length > 0;
  }

  // Durchlaufe die Zeilen im alten und neuen Code
  while (i < oldLines.length || j < newLines.length) {
    const oldLine = i < oldLines.length ? oldLines[i] : '';
    const newLine = j < newLines.length ? newLines[j] : '';

    // --- Pre-anchor: Anker wie findAnchor() position-unabhängig suchen (gleiche Zeile kann in new weiter unten stehen, z. B. DOCTYPE) ---
    if (!anchorFound) {
      // Gleiche Position + relevante Zeile
      if (i < oldLines.length && j < newLines.length && oldLine === newLine && isRelevantLine(newLine)) {
        anchorFound = true;
        for (let k = 0; k < i; k++) {
          mergedCode.push(oldLines[k]);
        }
        mergedCode.push(newLine);
        i++;
        j++;
        continue;
      }

      // Aktuelle alte Zeile kommt später in new vor (z. B. new hat zuerst <!DOCTYPE>)
      if (i < oldLines.length && isRelevantLine(oldLine)) {
        let newIdx = -1;
        for (let idx = j; idx < newLines.length; idx++) {
          if (newLines[idx] === oldLine) {
            newIdx = idx;
            break;
          }
        }
        if (newIdx >= 0) {
          anchorFound = true;
          for (let k = 0; k < i; k++) {
            mergedCode.push(oldLines[k]);
          }
          for (let k = j; k < newIdx; k++) {
            mergedCode.push(newLines[k]);
          }
          mergedCode.push(newLines[newIdx]);
          i++;
          j = newIdx + 1;
          continue;
        }
      }

      // Aktuelle neue Zeile kommt später in old vor (z. B. old hat Zeilen vor dem gemeinsamen Anker)
      if (j < newLines.length && i < oldLines.length && isRelevantLine(newLine)) {
        let oldIdx = -1;
        for (let idx = i; idx < oldLines.length; idx++) {
          if (oldLines[idx] === newLine) {
            oldIdx = idx;
            break;
          }
        }
        if (oldIdx >= 0) {
          anchorFound = true;
          for (let k = i; k < oldIdx; k++) {
            mergedCode.push(oldLines[k]);
          }
          mergedCode.push(newLine);
          i = oldIdx + 1;
          j++;
          continue;
        }
      }

      // Kein Anker an dieser Stelle: eine Position weiter (wie zuvor, aber seltener leerer Merge)
      if (i < oldLines.length) i++;
      if (j < newLines.length) j++;
      continue;
    }

    // Wenn der Anker gefunden wurde, setze den Vergleich wie gehabt fort
    if (anchorFound) {
      // Unveränderte Zeile
      if (oldLine === newLine) {
        //console.log(`Unverändert: ${newLine}`);
        mergedCode.push(newLine);
        highlightLine(i, "highlight-analyzing", 1000); // Markiert die Zeile für 1 Sekunde
        i++;
        j++;
      }
      // Verschobene Zeile erkannt
      else if (newLines.slice(j).includes(oldLine)) {
        const newIndex = newLines.indexOf(oldLine, j);
        //console.log(`Verschoben: ${oldLine}`);

        // Füge alle Zeilen zwischen der aktuellen Position und der verschobenen Zeile ein
        for (let k = j; k < newIndex; k++) {
          //console.log(`Hinzugefügt (dazwischen): ${newLines[k]}`);
          mergedCode.push(newLines[k]);
        }

        // Füge die verschobene Zeile hinzu
        mergedCode.push(oldLine);
        i++;
        j = newIndex + 1;
      }
      // Hinzugefügte Zeile
      else if (!oldLines.slice(i).includes(newLine)) {
        //console.log(`Hinzugefügt: ${newLine}`);
        mergedCode.push(newLine);
        //highlightLine(i, "highlight-new", 0); // Markiert die geänderte Zeile für 1 Sekunde
        i++;
        j++;
      }
      // Gelöschte Zeile
      else if (!newLines.slice(j).includes(oldLine)) {
        //console.log(`Gelöscht: ${oldLine}`);
        //highlightLine(i, "highlight-deleted", 0); // Markiert die gelöschte Zeile für 1 Sekunde
        i++;
      }
      // Geänderte Zeile
      else {
        //console.log(`Geändert: alt => ${oldLine} | neu => ${newLine}`);
        mergedCode.push(newLine);
        //highlightLine(i, "highlight-modified", 0); // Markiert die geänderte Zeile für 1 Sekunde
        i++;
        j++;
      }
    }
  }

  return mergedCode.join('\n');
}

/** compareAndMerge kann in Randfällen leer/kurz ausfallen; dann lieber vollständig ersetzen. */
function shouldFallbackCompareMergeResult(
  merged: string,
  oldCode: string,
  newCode: string
): boolean {
  const m = merged?.trim() ?? '';
  if (!m) return true;
  const oldLen = oldCode.trim().length;
  const newLen = newCode.trim().length;
  const minLen = Math.min(oldLen, newLen);
  if (minLen === 0) return false;
  return m.length < minLen * 0.1;
}

function highlightChanges(oldCode: string, newCode: string) {
  const oldLines = oldCode.split(/\r?\n/);
  const newLines = newCode.split(/\r?\n/);
  let i = 0, j = 0;

  while (i < oldLines.length || j < newLines.length) {
    if (i >= oldLines.length) {
      // Neue Zeile am Ende hinzugefügt
      highlightLine(j, "highlight-new", 0);
      j++;
    } else if (j >= newLines.length) {
      // Zeile am Ende gelöscht
      highlightLine(i, "highlight-modified", 0);
      i++;
    } else if (oldLines[i] === newLines[j]) {
      // Unveränderte Zeile
      i++;
      j++;
    } else if (newLines.slice(j).includes(oldLines[i])) {
      // Zeile hinzugefügt
      highlightLine(j, "highlight-new", 0);
      j++;
    } else if (oldLines.slice(i).includes(newLines[j])) {
      // Zeile gelöscht
      highlightLine(i, "highlight-modified", 0);
      i++;
    } else {
      // Zeile geändert
      highlightLine(j, "highlight-modified", 0);
      i++;
      j++;
    }
  }
}

/**
 * Prüft, ob ein Merge "einfach" ist und mit compareAndMerge behandelt werden kann.
 * Komplexe Fälle (z.B. Strukturwechsel) sollten mit LLM gemerged werden.
 */
function isSimpleMerge(oldCode: string, newCode: string, language: string): boolean {
  const oldLines = oldCode.split(/\r?\n/);
  const newLines = newCode.split(/\r?\n/);
  const oldLineCount = oldLines.length;
  const newLineCount = newLines.length;

  // Wenn alter Code leer ist, ist es einfach (wird ersetzt)
  if (!oldCode.trim()) {
    return true;
  }

  // Heuristik 1: Größenverhältnis
  // Wenn neuer Code > 10x größer, ist es wahrscheinlich ein Strukturwechsel
  const sizeRatio = newLineCount / Math.max(oldLineCount, 1);
  if (sizeRatio > 10) {
    console.log(`Merge zu komplex: Neuer Code ist ${sizeRatio.toFixed(1)}x größer`);
    return false;
  }

  // Heuristik 2: HTML/XML - DOCTYPE oder Root-Element erkannt
  if (language.toLowerCase() === 'html' || language.toLowerCase() === 'xml') {
    const hasDoctype = newCode.trim().toLowerCase().startsWith('<!doctype');
    const hasHtmlRoot = newCode.includes('<html') || newCode.includes('<HTML');

    // Wenn alter Code kein vollständiges HTML ist, aber neuer schon
    const oldIsCompleteHtml = oldCode.includes('<!doctype') || oldCode.includes('<html');

    if ((hasDoctype || hasHtmlRoot) && !oldIsCompleteHtml && oldLineCount < 10) {
      console.log('Merge zu komplex: Neuer Code ist vollständige HTML-Datei, alter Code ist Fragment');
      return false;
    }
  }

  // Heuristik 3: Struktur-Erkennung für verschiedene Sprachen
  const structureKeywords = {
    'html': ['<!doctype', '<html', '<head', '<body'],
    'python': ['#!/usr/bin/env', 'import ', 'from ', 'class ', 'def '],
    'javascript': ['import ', 'export ', 'const ', 'function ', 'class '],
    'typescript': ['import ', 'export ', 'interface ', 'type ', 'class '],
    'jsx': ['import ', 'export ', 'function ', 'const ', 'class '],
    'tsx': ['import ', 'export ', 'interface ', 'type ', 'function ', 'const '],
  };

  const keywords = structureKeywords[language.toLowerCase()] || [];
  const newHasStructure = keywords.some(kw =>
    newCode.toLowerCase().includes(kw.toLowerCase())
  );
  const oldHasStructure = keywords.some(kw =>
    oldCode.toLowerCase().includes(kw.toLowerCase())
  );

  // Wenn neuer Code Struktur hat, alter aber nicht, und alter Code sehr klein
  if (newHasStructure && !oldHasStructure && oldLineCount < 5) {
    console.log('Merge zu komplex: Neuer Code hat Datei-Struktur, alter Code ist Fragment');
    return false;
  }

  // Heuristik 4: Ähnlichkeits-Score
  // Wenn weniger als 20% der Zeilen übereinstimmen und großer Größenunterschied
  const matchingLines = oldLines.filter(line =>
    line.trim() && newLines.includes(line)
  ).length;
  const similarity = matchingLines / Math.max(oldLineCount, 1);

  if (similarity < 0.2 && sizeRatio > 3) {
    console.log(`Merge zu komplex: Nur ${(similarity * 100).toFixed(0)}% Ähnlichkeit bei ${sizeRatio.toFixed(1)}x Größenunterschied`);
    return false;
  }

  // Heuristik 5: Sehr kleiner alter Code (< 5 Zeilen) + großer neuer Code
  if (oldLineCount <= 5 && newLineCount > 50) {
    console.log('Merge zu komplex: Alter Code ist sehr klein, neuer Code ist vollständige Datei');
    return false;
  }

  // Alle Heuristiken bestanden → einfacher Merge
  return true;
}

/**
 * Nutzt LLM über /task API, um komplexe Code-Merges durchzuführen
 */
async function mergeCodeWithLLM(oldCode: string, newCode: string, language: string): Promise<string> {
  try {
    const { executeTaskRequest } = await import('../chatrequests');
    const result = await executeTaskRequest({
      name: 'code-merge',
      oldCode: oldCode,
      newCode: newCode,
      language: language
    });
    // Die Antwort kann in verschiedenen Formaten kommen
    let mergedCode = result.message || result.text || result.result || '';

    // Entferne mögliche Code-Block-Markierungen
    mergedCode = mergedCode.replace(/^```[\w]*\n?/gm, '').replace(/\n?```$/gm, '').trim();

    if (!mergedCode) {
      throw new Error('LLM hat keinen Code zurückgegeben');
    }

    console.log('LLM Merge erfolgreich');
    return mergedCode;
  } catch (error) {
    console.error('LLM Merge Error:', error);
    // Fallback: Versuche compareAndMerge oder ersetze komplett
    throw error;
  }
}

export const insertCode = async (lang, code, id) => {
  const editorState = getViewData(EDITOR_VIEW_NAME);
  console.log('insert code', lang, code, editorState.data?.editorCode);

  const oldEditorCode = editorState.data?.editorCode || "";

  // VERIFICATION: echte Steuerzeichen (nicht die Literale "\\r" / "\\n")
  console.log("--- BYTE-LEVEL CR/LF VERIFICATION ---");
  console.log("oldEditorCode contains CR:", oldEditorCode.includes("\r"));
  console.log("oldEditorCode contains LF:", oldEditorCode.includes("\n"));
  console.log("oldEditorCode line count (by LF):", oldEditorCode.split(/\n/).length);
  console.log("oldEditorCode line count (by CRLF split):", oldEditorCode.split(/\r\n/).length);

  console.log("new code (from KiRa) contains CR:", code.includes("\r"));
  console.log("new code (from KiRa) contains LF:", code.includes("\n"));
  console.log("--- END VERIFICATION ---");

  let mergedCode;

  // Prüfe, ob alter Code leer ist → direkt ersetzen
  if (oldEditorCode.trim() === "") {
    mergedCode = replaceEntireCode(code);
  }
  // Prüfe, ob es ein einfacher Merge ist
  else if (isSimpleMerge(oldEditorCode, code, lang)) {
    // Einfacher Merge: Nutze compareAndMerge wenn Anker gefunden, sonst anhängen
    if (findAnchor(oldEditorCode, code)) {
      console.log('Einfacher Merge mit compareAndMerge');
      mergedCode = compareAndMerge(oldEditorCode, code);
      if (shouldFallbackCompareMergeResult(mergedCode, oldEditorCode, code)) {
        console.warn('compareAndMerge: Verdächtig kurzes Ergebnis, Fallback auf kompletten Ersatz');
        mergedCode = replaceEntireCode(code);
      }
    } else {
      console.log('Einfacher Merge: Code anhängen');
      mergedCode = appendCode(oldEditorCode, code);
    }
  }
  // Komplexer Merge: Nutze LLM
  else {
    console.log('Komplexer Merge: Nutze LLM');
    try {
      mergedCode = await mergeCodeWithLLM(oldEditorCode, code, lang);
      // Highlight alle neuen Zeilen
      const lines = mergedCode.split(/\r?\n/);
      lines.forEach((line, index) => {
        highlightLine(index, "highlight-new", 3000);
      });
    } catch (error) {
      console.error('LLM Merge fehlgeschlagen, Fallback auf compareAndMerge:', error);
      // Fallback: Versuche compareAndMerge oder ersetze komplett
      if (findAnchor(oldEditorCode, code)) {
        mergedCode = compareAndMerge(oldEditorCode, code);
        if (shouldFallbackCompareMergeResult(mergedCode, oldEditorCode, code)) {
          console.warn('compareAndMerge (LLM-Fallback): Verdächtig kurzes Ergebnis, Fallback auf kompletten Ersatz');
          mergedCode = replaceEntireCode(code);
        }
      } else {
        // Als letzter Fallback: komplett ersetzen
        mergedCode = replaceEntireCode(code);
      }
    }
  }

  const newEditorState = {
    ...editorState,
    data: {
      ...(editorState.data || {}),
      editorCode: mergedCode,
      oldEditorCode: oldEditorCode,
      codeInsertId: id
    }
  };

  setViewData(EDITOR_VIEW_NAME, newEditorState);

  // Führe das Highlighting nach dem Setzen des neuen Codes durch
  setTimeout(() => {
    highlightChanges(oldEditorCode, mergedCode);
  }, 500);
}

export const undoInsertCode = () => {
  const editorState = getViewData(EDITOR_VIEW_NAME);
  console.log('undo insert code', editorState.data?.editorCode);
  removeAllHighlights();
  const newEditorState = {
    ...editorState,
    data: {
      ...(editorState.data || {}),
      editorCode: editorState.data?.oldEditorCode || "",
      oldEditorCode: undefined
    }
  };
  setViewData(EDITOR_VIEW_NAME, newEditorState);
}

